import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormField,
  FormItem,
  FormMessage,
  FormControl,
  FormLabel,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { NumericFormat } from 'react-number-format';
import { Switch } from '@/components/ui/switch';
import { useState } from 'react';

type FormTaskAdjustSpotProps = {
  defaultValues?: {
    max_spending: number;
  };
  onSubmit: (data: z.infer<typeof formSchema>) => void;
  children?: React.ReactNode;
};

const formSchema = z.object({
  max_spending: z
    .number({ message: 'Max spending is required' })
    .refine((value) => value === -1 || value > 0, {
      message: 'Max spending must be greater than 0',
    })
});

const FormTaskAdjustSpot = ({
  onSubmit,
  defaultValues,
  children,
}: FormTaskAdjustSpotProps) => {
  const [isMaxSpendingEnabled, setIsMaxSpendingEnabled] = useState(
    defaultValues?.max_spending !== -1 &&
      defaultValues?.max_spending !== undefined
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      max_spending: defaultValues?.max_spending,
    },
  });

  const handleSubmit = (data: z.infer<typeof formSchema>) => {
    onSubmit({
      max_spending: isMaxSpendingEnabled ? data.max_spending : -1,
    });
  };

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="grid gap-4">
            <FormField
              control={form.control}
              name="max_spending"
              render={({ field }) => (
                <FormItem>
                  <div className="mb-2 flex items-center justify-between">
                    <FormLabel>Budget limit</FormLabel>
                    <Switch
                      checked={isMaxSpendingEnabled}
                      onCheckedChange={(checked) => {
                        setIsMaxSpendingEnabled(checked as boolean);
                      }}
                    />
                  </div>
                  {!isMaxSpendingEnabled ? null : (
                    <FormControl>
                      <NumericFormat
                        customInput={Input}
                        thousandSeparator
                        allowNegative={false}
                        // decimalScale={4}
                        value={field.value}
                        onValueChange={(v) => field.onChange(v.floatValue)}
                      />
                    </FormControl>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {children}
        </form>
      </Form>
    </div>
  );
};

export default FormTaskAdjustSpot;
